// VariantTab.jsx
import React, { useEffect, useRef, useState } from "react";
import { Table, Form, Col, Row, InputGroup, Button } from "react-bootstrap";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useFormContext, useFieldArray } from "react-hook-form";
import usePostRequest from "../../hooks/usePostRequest";
import { API_ENDPOINTS } from "../../constant/_constant";

function VariantTab() {
  const {
    register,
    control,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "variants",
  });

  const nextSeq = watch("variantNextSeq");
  const formVendorSku = watch("vendor_sku");
  const baseDescription = watch("description");
  const baseName = watch("name");
  const baseUm = watch("uoi");
  const baseSalesDescription = watch("salesDescription");
  const baseCost = watch("cost");
  const baseCustomerPrice = watch("customer_price");

  // VariantTab.jsx
  const [newVariant, setNewVariant] = useState({
    variantSku: "",
  });
  const [addError, setAddError] = useState("");
  const [rowErrors, setRowErrors] = useState({});

  const nextSeqRef = useRef(1);

  // useEffect(() => {
  //   const existing = getValues("variants") || [];
  //   const maxSeq = existing.reduce((max, v) => {
  //     const match = v?.internalId?.match(/-(\d+)$/);
  //     const seq = match ? parseInt(match[1], 10) : 0;
  //     return seq > max ? seq : max;
  //   }, 0);
  //   nextSeqRef.current = maxSeq + 1;
  // }, []);

  const isDuplicateSku = (value, excludeIndex) => {
    const trimmed = value?.trim().toLowerCase();
    if (!trimmed) return false;
    const currentValues = getValues("variants") || [];
    return currentValues.some(
      (v, idx) =>
        idx !== excludeIndex && v?.variantSku?.trim().toLowerCase() === trimmed,
    );
  };

  const generateInternalId = (seq) => {
    const base = formVendorSku || "";
    return `${base}-${String(seq).padStart(3, "0")}`;
  };

  useEffect(() => {
    if (getValues("variantNextSeq") != null) return;
    const existing = getValues("variants") || [];
    const maxSeq = existing.reduce((max, v) => {
      const match = v?.internalId?.match(/-(\d+)$/);
      const seq = match ? parseInt(match[1], 10) : 0;
      return seq > max ? seq : max;
    }, 0);
    setValue("variantNextSeq", maxSeq + 1);
  }, []);

  const handleAddVariant = () => {
    if (!newVariant.variantSku) return;
    if (isDuplicateSku(newVariant.variantSku)) {
      setAddError("This vendor variant SKU already exists.");
      return;
    }
    setAddError("");
    const seq = getValues("variantNextSeq") || 1;
    append({
      variantSku: newVariant.variantSku,
      internalId: generateInternalId(seq),
      description: baseDescription || "",
      name: newVariant.variantSku,
      um: baseUm || "",
      salesDescription: baseSalesDescription || "",
      cost: baseCost || "",
      customerPrice: baseCustomerPrice || "",
    });
    setValue("variantNextSeq", seq + 1);
    setNewVariant({ variantSku: "" });
  };

  // const handleAddVariant = () => {
  //   if (!newVariant.variantSku) return;
  //   if (isDuplicateSku(newVariant.variantSku)) {
  //     setAddError("This variant SKU already exists.");
  //     return;
  //   }
  //   setAddError("");
  //   append({
  //     variantSku: newVariant.variantSku,
  //     internalId: generateInternalId(nextSeqRef.current),
  //     description: baseDescription || "",
  //     salesDescription: baseSalesDescription || "",
  //     cost: baseCost || "",
  //     customerPrice: baseCustomerPrice || "",
  //   });
  //   nextSeqRef.current += 1;
  //   setNewVariant({ variantSku: "" });
  // };

  const handleRemoveVariant = (index) => {
    remove(index);
    setRowErrors((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  const handleRowSkuBlur = (index, value) => {
    if (isDuplicateSku(value, index)) {
      setRowErrors((prev) => ({
        ...prev,
        [index]: "This variant already exists.",
      }));
    } else {
      setRowErrors((prev) => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
    }
  };

  const textareaRefs = useRef({});

  const mergeRefs =
    (...refs) =>
    (el) => {
      refs.forEach((r) => {
        if (typeof r === "function") r(el);
        else if (r && typeof r === "object") r.current = el;
      });
    };

  useEffect(() => {
    Object.values(textareaRefs.current).forEach((el) => {
      if (el) {
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
      }
    });
  }, [fields.length]);

  return (
    <div>
      <Row className="mb-3">
        <Col className="mob_50">
          <Form.Group>
            <Form.Label className="">Variant</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Enter Variant"
                value={newVariant.variantSku}
                onChange={(e) => {
                  setNewVariant((prev) => ({
                    ...prev,
                    variantSku: e.target.value,
                  }));
                  if (addError) setAddError("");
                }}
              />
            </InputGroup>
            {addError && <span className="text-danger">{addError}</span>}
          </Form.Group>
        </Col>
        <Col xs="auto" className="d-flex align-items-start">
          <Form.Group>
            <Form.Label className="d-block invisible">Add</Form.Label>
            <Button variant="warning" onClick={handleAddVariant}>
              <IconPlus size={20} /> Add Variant
            </Button>
          </Form.Group>
        </Col>
      </Row>

      {fields.length > 0 && (
        <div className="table-responsive">
          <Table>
            <thead>
              <tr>
                <th>VENDOR VARIANT SKU</th>
                <th>INTERNAL VARIANT SKU</th>
                <th>NAME</th>
                <th>UM</th>
                <th>DESCRIPTION (PURCHASE)</th>
                <th>DESCRIPTION (SALES)</th>
                <th>COST</th>
                <th>CUSTOMER PRICE</th>
                <th>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field, index) => (
                <tr key={field.id}>
                  <td>
                    <Form.Control
                      type="text"
                      {...register(`variants[${index}].variantSku`)}
                      defaultValue={field.variantSku}
                      onBlur={(e) => handleRowSkuBlur(index, e.target.value)}
                    />
                    {rowErrors[index] && (
                      <span className="text-danger">{rowErrors[index]}</span>
                    )}
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      {...register(`variants[${index}].internalId`)}
                      defaultValue={field.internalId}
                      readOnly
                      tabIndex={-1}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      {...register(`variants[${index}].name`)}
                      defaultValue={field.name}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      {...register(`variants[${index}].um`)}
                      defaultValue={field.um}
                    />
                  </td>
                  <td>
                    <Form.Control
                      as="textarea"
                      rows={1}
                      {...register(`variants[${index}].description`)}
                      ref={mergeRefs(
                        register(`variants[${index}].description`).ref,
                        (el) => {
                          textareaRefs.current[`desc-${index}`] = el;
                        },
                      )}
                      defaultValue={field.description}
                      style={{
                        height: "auto",
                        minHeight: "38px",
                        resize: "none",
                        overflow: "hidden",
                      }}
                      onInput={(e) => {
                        e.target.style.height = "auto";
                        e.target.style.height = `${e.target.scrollHeight}px`;
                      }}
                    />
                  </td>
                  <td>
                    <Form.Control
                      as="textarea"
                      rows={1}
                      {...register(`variants[${index}].salesDescription`)}
                      ref={mergeRefs(
                        register(`variants[${index}].salesDescription`).ref,
                        (el) => {
                          textareaRefs.current[`sales-${index}`] = el;
                        },
                      )}
                      defaultValue={field.salesDescription}
                      style={{
                        height: "auto",
                        minHeight: "38px",
                        resize: "none",
                        overflow: "hidden",
                      }}
                      onInput={(e) => {
                        e.target.style.height = "auto";
                        e.target.style.height = `${e.target.scrollHeight}px`;
                      }}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      step="any"
                      {...register(`variants[${index}].cost`)}
                      defaultValue={field.cost}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      step="any"
                      {...register(`variants[${index}].customerPrice`)}
                      defaultValue={field.customerPrice}
                    />
                  </td>
                  <td>
                    <span
                      onClick={() => handleRemoveVariant(index)}
                      className="avatar bg-red-lt"
                      style={{ cursor: "pointer" }}
                    >
                      <IconTrash />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default VariantTab;
